import { useEffect, useMemo, useState } from "react";
import { BookOpen } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, TableSkeleton } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

export default function BiblePage() {
  const [books, setBooks] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [testament, setTestament] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/bible/books"), api.get("/bible/translations")]).then(([b, t]) => {
      setBooks(b.data);
      setTranslations(t.data);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    return books.filter((b) => {
      if (testament !== "all" && b.testament !== testament) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return b.name.toLowerCase().includes(q) || (b.abbreviation || "").toLowerCase().includes(q);
    });
  }, [books, testament, query]);

  return (
    <>
      <PageHeader
        title="Bible"
        description="Books and translations available in the platform."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
        <Card className="p-4 border-border/70 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total books</p>
          <p className="mt-1 text-2xl font-semibold">{books.length}</p>
        </Card>
        <Card className="p-4 border-border/70 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Old Testament</p>
          <p className="mt-1 text-2xl font-semibold">{books.filter((b) => b.testament === "OT").length}</p>
        </Card>
        <Card className="p-4 border-border/70 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Translations</p>
          <p className="mt-1 text-2xl font-semibold">{translations.length}</p>
          <p className="mt-1 text-xs text-muted-foreground line-clamp-1">
            {translations.map((t) => t.name || t.id).join(" · ")}
          </p>
        </Card>
      </div>

      <Card className="border-border/70 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-border">
          <Input
            placeholder="Search books…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="bible-search"
            className="flex-1"
          />
          <div className="flex gap-1.5">
            {[
              { key: "all", label: "All" },
              { key: "OT", label: "Old Testament" },
              { key: "NT", label: "New Testament" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setTestament(opt.key)}
                data-testid={`testament-${opt.key}`}
                className={
                  "px-3 py-1.5 text-xs font-medium rounded-md border transition-colors " +
                  (testament === opt.key
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background border-border text-muted-foreground hover:text-foreground")
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-4">
            <TableSkeleton rows={6} cols={4} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={BookOpen}
              title="No books match"
              description="Try a different search or testament filter."
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-4">
            {filtered.map((b) => (
              <div
                key={b.id}
                data-testid={`book-${b.id}`}
                className="rounded-lg border border-border bg-background hover:bg-muted/40 transition-colors p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-muted-foreground">{b.abbreviation}</span>
                  <Badge variant="outline" className="text-[10px] py-0">
                    {b.testament}
                  </Badge>
                </div>
                <p className="font-medium leading-tight">{b.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{b.chapters} chapters</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
